import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SignupRequest } from '../models/signup';
import { FeedbackRequest } from '../models/feedback';
import { InvestorContactRequest } from '../models/investor-contact';
import { environment } from '../../environments/environment';
import { UtmParams } from './utm.service';

export interface ErrorResponse {
  error?: string;
  message?: string;
  details?: { [key: string]: string[] };
  requestId?: string;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignupService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  signup(request: SignupRequest, utms: UtmParams): Observable<any> {
    // Omit params that were never captured rather than sending them — the backend already
    // buckets "no utm.source at all" as Direct traffic; sending literal placeholder values
    // would make that indistinguishable from a real campaign tagged utm_source=direct.
    const q = new URLSearchParams({
      ...(utms.utm_source   ? { utm_source:   utms.utm_source }   : {}),
      ...(utms.utm_medium   ? { utm_medium:   utms.utm_medium }   : {}),
      ...(utms.utm_campaign ? { utm_campaign: utms.utm_campaign } : {}),
      ...(utms.utm_term     ? { utm_term:     utms.utm_term }     : {}),
      ...(utms.utm_content  ? { utm_content:  utms.utm_content }  : {}),
    });
    const url = `${this.apiBaseUrl}/signups?${q.toString()}`;
    return this.http.post(url, request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Parse and enhance error response
        const errorResponse: ErrorResponse = this.parseErrorResponse(error);
        return throwError(() => errorResponse);
      })
    );
  }

  submitFeedback(request: FeedbackRequest): Observable<any> {
    const url = `${this.apiBaseUrl}/feedback`;
    return this.http.post(url, request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Parse and enhance error response
        const errorResponse: ErrorResponse = this.parseErrorResponse(error);
        return throwError(() => errorResponse);
      })
    );
  }

  submitInvestorContact(request: InvestorContactRequest): Observable<{ success: boolean; message: string }> {
    const url = `${this.apiBaseUrl}/contact`;
    return this.http.post<{ success: boolean; message: string }>(url, request).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorResponse: ErrorResponse = this.parseErrorResponse(error);
        return throwError(() => errorResponse);
      })
    );
  }

  private parseErrorResponse(error: HttpErrorResponse): ErrorResponse {
    // Try to parse the error response body
    let errorResponse: ErrorResponse = {
      error: 'UnknownError',
      message: 'An unexpected error occurred. Please try again later.'
    };

    if (error.error) {
      // If error.error is already an object (parsed JSON)
      if (typeof error.error === 'object' && !Array.isArray(error.error)) {
        errorResponse = {
          error: error.error.error || errorResponse.error,
          message: error.error.message || errorResponse.message,
          details: error.error.details,
          requestId: error.error.requestId,
          timestamp: error.error.timestamp
        };
      } 
      // If error.error is a string (unparsed JSON or plain text)
      else if (typeof error.error === 'string') {
        try {
          const parsed = JSON.parse(error.error);
          errorResponse = {
            error: parsed.error || errorResponse.error,
            message: parsed.message || errorResponse.message,
            details: parsed.details,
            requestId: parsed.requestId,
            timestamp: parsed.timestamp
          };
        } catch {
          // If parsing fails, use the string as the message
          errorResponse.message = error.error;
        }
      }
    }

    // Handle specific HTTP status codes
    if (error.status === 0) {
      errorResponse.error = 'NetworkError';
      errorResponse.message = 'Unable to connect to the server. Please check your internet connection and try again.';
    } else if (error.status === 400) {
      errorResponse.error = errorResponse.error || 'ValidationError';
      if (!errorResponse.message || errorResponse.message === 'An unexpected error occurred. Please try again later.') {
        errorResponse.message = 'Please check your information and try again.';
      }
    } else if (error.status === 429) {
      errorResponse.error = 'RateLimitError';
      errorResponse.message = 'Too many requests. Please wait a moment and try again.';
    } else if (error.status === 408 || error.status === 504) {
      errorResponse.error = 'TimeoutError';
      errorResponse.message = 'The request took too long. Please try again.';
    } else if (error.status >= 500) {
      errorResponse.error = errorResponse.error || 'ServerError';
      if (!errorResponse.message || errorResponse.message === 'An unexpected error occurred. Please try again later.') {
        errorResponse.message = 'The server encountered an error. Please try again later.';
      }
    }

    return errorResponse;
  }
}

